import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { getUserFacingErrorMessage } from '@brokerportal/common/utils/error.utils';
import { of } from 'rxjs';
import { catchError, finalize, startWith, switchMap } from 'rxjs/operators';
import { CecInstallerModel, QuoteFormModel, Scheme } from '../models';
import { QuoteService } from '../services';

export interface QuoteInstallerDetailsDialogData {
    quoteExternalId: string;
    scope: Scheme;
    accreditationNumbers?: string[];
}

@Component({
    selector: 'bp-quote-installer-details-dialog',
    templateUrl: './quote-installer-details-dialog.component.html',
    styleUrls: ['./quote-installer-details-dialog.component.scss']
})
export class QuoteInstallerDetailsDialogComponent implements OnInit {
    @ViewChildren('accreditationInput') accreditationInputs: QueryList<ElementRef<HTMLInputElement>>;

    isLoading = false;
    readonly maxInstallers = 2;

    readonly installerForm: FormGroup = this.fb.group({
        installers: this.fb.array([]),
        confirmInformation: [false, Validators.requiredTrue]
    });

    get installersFormArray(): FormArray {
        return this.installerForm.get('installers') as FormArray;
    }

    constructor(
        public dialogRef: MatDialogRef<QuoteInstallerDetailsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: QuoteInstallerDetailsDialogData,
        private readonly fb: FormBuilder,
        private readonly snackBar: MatSnackBar,
        private readonly quoteService: QuoteService
    ) {}

    ngOnInit(): void {
        const existingNumbers = this.data.accreditationNumbers;

        if (existingNumbers && existingNumbers.length > 0) {
            existingNumbers.forEach(number => this.addInstaller(number));
            this.installerForm.get('confirmInformation').setValue(false);
        } else {
            this.addInstaller();
        }
    }

    addInstaller(initialNumber: string | null = null): void {
        if (this.installersFormArray.length >= this.maxInstallers) {
            return;
        }

        const newInstallerGroup = this.fb.group({
            name: [{ value: null, disabled: true }],
            accreditationNumber: new FormControl(initialNumber, {
                validators: [Validators.required],
                updateOn: 'blur'
            }),
            isLoading: [false]
        });

        const accreditationNumberCtrl = newInstallerGroup.get('accreditationNumber');
        accreditationNumberCtrl.valueChanges
            .pipe(
                startWith(accreditationNumberCtrl.value),
                switchMap(number => {
                    const nameCtrl = newInstallerGroup.get('name');
                    nameCtrl.setValue(null);

                    if (accreditationNumberCtrl.invalid || !number) {
                        return of(null);
                    }

                    newInstallerGroup.get('isLoading').setValue(true);
                    return this.quoteService.getCecInstallerByAccreditationNumber(number).pipe(
                        catchError(() => of(null)),
                        finalize(() => newInstallerGroup.get('isLoading').setValue(false))
                    );
                })
            )
            .subscribe((result: CecInstallerModel | null) => {
                if (result) {
                    newInstallerGroup.get('name').setValue(`${result.firstName} ${result.lastName}`);
                    accreditationNumberCtrl.setErrors(null);
                } else {
                    if (accreditationNumberCtrl.value) {
                        accreditationNumberCtrl.setErrors({ notFound: true });
                    }
                }
            });

        this.installersFormArray.push(newInstallerGroup);

        if (!initialNumber) {
            setTimeout(() => this.accreditationInputs.last?.nativeElement.focus());
        }
    }

    removeInstaller(index: number): void {
        this.installersFormArray.removeAt(index);
    }

    onSubmit(): void {
        this.installerForm.markAllAsTouched();
        if (this.installerForm.invalid) {
            return;
        }

        this.isLoading = true;

        const accreditationNumbers = this.installersFormArray.controls
            .map(control => control.get('accreditationNumber').value)
            .filter(num => !!num);

        this.quoteService
            .patchAccreditationNumbers(this.data.quoteExternalId, accreditationNumbers, this.data.scope)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (updatedQuote: QuoteFormModel) => {
                    this.snackBar.open('Installer details submitted successfully.', 'Dismiss', { duration: 5000 });
                    this.dialogRef.close(updatedQuote);
                },
                error: (error: HttpErrorResponse) => {
                    const errorMessage = getUserFacingErrorMessage(
                        error,
                        'Failed to submit installer details. Please try again.'
                    );

                    this.snackBar.open(errorMessage, 'Dismiss', {
                        duration: 10000,
                        panelClass: 'error-snackbar'
                    });
                }
            });
    }

    onCancel(): void {
        this.dialogRef.close();
    }
}
